import cx from 'classnames';
import { useCallback, type FunctionComponent } from 'react';

import CalendarPlusIcon from '@atlaskit/icon/core/calendar-plus';
import CloseIcon from '@atlaskit/icon/core/close';
import MergeSuccessIcon from '@atlaskit/icon/core/merge-success';
import { Analytics } from '@trello/atlassian-analytics';
import {
  smartScheduledCardsSharedState,
  type SmartScheduledCardPlannerEvent,
} from '@trello/business-logic/planner';
import { useFeatureGate } from '@trello/feature-gate-client';
import { client } from '@trello/graphql';
import { Spinner } from '@trello/nachos/spinner';
import { useSharedStateSelector } from '@trello/shared-state';
import {
  useIsPlannerPanelOpen,
  useSplitScreenSharedState,
} from '@trello/split-screen';

import {
  IslandNav,
  IslandNavButton,
  IslandNavSeparator,
} from 'app/src/components/IslandNav';
import { PROPOSED_EVENT_ID_PREFIX } from 'app/src/components/Planner/constants';
import { bulkActionSelectedCardsSharedState } from './bulkActionSelectedCardsSharedState';
import { SmartScheduleCardFragmentDoc } from './SmartScheduleCardFragment.generated';
import { useSmartScheduleCardsMutation } from './SmartScheduleCardsMutation.generated';
import { SmartScheduleIslandNavigation } from './SmartScheduleIslandNavigation';
import { useIsBulkActionEnabled } from './useIsBulkActionEnabled';

import * as styles from './BulkActionIsland.module.less';

export interface BulkActionIslandProps {
  isVisible?: boolean;
}

export const BulkActionIsland: FunctionComponent<BulkActionIslandProps> = ({
  isVisible = false,
}) => {
  const isBulkActionEnabled = useIsBulkActionEnabled();
  const { value: isSmartScheduleEnabled } = useFeatureGate(
    'phx_smart_schedule_m1',
  );

  const [smartScheduleCards, { loading: isSmartScheduleLoading }] =
    useSmartScheduleCardsMutation();

  const numProposedEvents = useSharedStateSelector(
    smartScheduledCardsSharedState,
    useCallback((state) => state.proposedEvents.length, []),
  );

  const isPlannerPanelOpen = useIsPlannerPanelOpen();
  const { togglePlanner } = useSplitScreenSharedState();

  const unSelectAllCards = useCallback(() => {
    bulkActionSelectedCardsSharedState.setValue(() => ({}));
  }, []);

  const handleSchedule = useCallback(() => {
    const traceId = Analytics.startTask({
      taskName: 'edit-plannerEvent/smartSchedule',
      source: 'bulkActionIsland',
    });
    const allCardIds = Object.values(
      // get all objects for each board
      bulkActionSelectedCardsSharedState.value,
    ).reduce((acc, curr) => {
      // get all card IDs that are `true` for the board
      Object.keys(curr).forEach((cardId) => {
        if (curr[cardId]) {
          acc.push(cardId);
        }
      });
      return acc;
    }, [] as string[]);
    smartScheduleCards({
      variables: {
        cardIds: allCardIds,
        traceId,
      },
    })
      .then(({ data }) => {
        const proposedEvents = data?.smartScheduleCards?.scheduled_cards?.map(
          (card) => {
            const cardData = client.readFragment({
              id: `Card:${card.card_id}`,
              fragment: SmartScheduleCardFragmentDoc,
            });
            return {
              id: card.card_id,
              start: card.start_time,
              end: card.end_time,
              duration: card.estimated_duration_minutes,
              eventId: PROPOSED_EVENT_ID_PREFIX + card.card_id,
              title: cardData?.name,
              durationEditable: true,
              startEditable: true,
            } as SmartScheduledCardPlannerEvent;
          },
        );
        smartScheduledCardsSharedState.setValue({
          activeIndex: 0,
          proposedEvents: [
            ...smartScheduledCardsSharedState.value.proposedEvents,
            ...(proposedEvents ?? []),
          ],
        });
        Analytics.taskSucceeded({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'bulkActionIsland',
        });
        if (proposedEvents?.length && !isPlannerPanelOpen) {
          togglePlanner();
        }
        unSelectAllCards();
      })
      .catch((error) => {
        Analytics.taskFailed({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'bulkActionIsland',
          error,
        });
      });
  }, [smartScheduleCards, unSelectAllCards, isPlannerPanelOpen, togglePlanner]);

  if (!isBulkActionEnabled) {
    return null;
  }

  if (isSmartScheduleLoading) {
    return (
      <IslandNav
        buttonGroupClassName={styles.buttonGroup}
        className={cx(styles.bulkActionIsland, {
          [styles.visible]: isVisible,
          [styles.hidden]: !isVisible,
        })}
      >
        <Spinner isAi />
      </IslandNav>
    );
  }

  if (numProposedEvents > 0) {
    return (
      <IslandNav
        buttonGroupClassName={styles.buttonGroup}
        className={cx(styles.bulkActionIsland, {
          [styles.visible]: isVisible,
          [styles.hidden]: !isVisible,
        })}
      >
        <SmartScheduleIslandNavigation />
      </IslandNav>
    );
  }

  return (
    <IslandNav
      buttonGroupClassName={styles.buttonGroup}
      className={cx(styles.bulkActionIsland, {
        [styles.visible]: isVisible,
        [styles.hidden]: !isVisible,
      })}
    >
      {isSmartScheduleEnabled && (
        <IslandNavButton
          icon={<CalendarPlusIcon label="Schedule" />}
          onClick={handleSchedule}
          title="Schedule"
          label="Schedule"
          showLabels={true}
        />
      )}
      <IslandNavButton
        icon={<MergeSuccessIcon label="Merge" />}
        onClick={() => {}}
        title="Merge"
        label="Merge"
        showLabels={true}
        isDisabled={false}
      />
      <IslandNavSeparator />
      <IslandNavButton
        icon={<CloseIcon label="Unselect" />}
        onClick={unSelectAllCards}
        title="Unselect"
        label="Unselect"
        showLabels={true}
      />
    </IslandNav>
  );
};
